import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  addObservationApi,
  medicalObservationListApi,
  fetchTimelineObservations,
  createTimelineObservation,
  updateTimelineObservation,
  deleteTimelineObservation,
  fetchTimelineEtapes,
  createTimelineEtape,
  deleteTimelineEtape,
  fetchTimelineCards,
  createTimelineCard,
  updateTimelineCard,
  deleteTimelineCard,
  fetchTimelineTemplates,
  fetchTimelineTemplate,
  getDoctorExaminationsApi,
  fetchDoctorServices,
} from '../services/observationService';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch medical observation list for a specific appointment and scope (all | only_me).
 * Cached for 5 minutes, then stale and removed without persistent dehydration.
 */
export function useMedicalObservationList(appointmentId, type = 'only_me', options = {}) {
  const normalizedType = typeof type === 'boolean' ? (type ? 'all' : 'only_me') : (type || 'only_me');

  return useQuery({
    queryKey: queryKeys.observations.byAppointment(appointmentId, normalizedType),
    queryFn: async ({ signal }) => {
      const formData = new FormData();
      formData.append('appointment_id', appointmentId);
      formData.append('type', normalizedType);

      const response = await medicalObservationListApi(formData, { signal });
      if (response?.data?.res === true) {
        return response.data.data ?? [];
      }
      return response?.data?.data ?? [];
    },
    enabled: Boolean(appointmentId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000,    // 5 minutes (remove after 5 min)
    refetchOnWindowFocus: false,
    ...options,
  });
}

/**
 * Imperative fetch helper for medical observations
 */
export async function fetchMedicalObservations(appointmentId, type = 'only_me', queryClient = null) {
  const normalizedType = typeof type === 'boolean' ? (type ? 'all' : 'only_me') : (type || 'only_me');

  const queryFn = async () => {
    const formData = new FormData();
    formData.append('appointment_id', appointmentId);
    formData.append('type', normalizedType);

    const response = await medicalObservationListApi(formData);
    if (response?.data?.res === true) {
      return response.data.data ?? [];
    }
    return response?.data?.data ?? [];
  };

  if (queryClient) {
    return queryClient.fetchQuery({
      queryKey: queryKeys.observations.byAppointment(appointmentId, normalizedType),
      queryFn,
      staleTime: 5 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
    });
  }

  return queryFn();
}

/**
 * Fetch medical observations for a patient
 */
export function useMedicalObservations(patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.observations.byPatient(patientId),
    queryFn: async ({ signal }) => {
      const response = await medicalObservationListApi({ patient_id: patientId, signal });
      return response?.data?.data ?? response?.data ?? [];
    },
    enabled: Boolean(patientId),
    ...options,
  });
}

/**
 * Fetch timeline observations for a patient
 */
export function useTimelineObservations(patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.observations.timeline(patientId),
    queryFn: async ({ signal }) => {
      const response = await fetchTimelineObservations(patientId);
      return response?.data?.data ?? response?.data ?? [];
    },
    enabled: Boolean(patientId),
    ...options,
  });
}

/**
 * Fetch timeline etapes for a patient
 */
export function useTimelineEtapes(patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.observations.etapes(patientId),
    queryFn: async () => {
      const response = await fetchTimelineEtapes(patientId);
      return response?.data?.data ?? response?.data ?? [];
    },
    enabled: Boolean(patientId),
    ...options,
  });
}

/**
 * Fetch timeline cards catalog for a doctor with 24-hour persistent caching.
 * Cards are doctor-scoped configuration items, rarely changed during sessions.
 *
 * @param {number|string} doctorId - The doctor's unique identifier
 * @param {object} options - Additional TanStack Query options
 */
export function useTimelineCards(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.observations.cards(doctorId),
    queryFn: async () => {
      const list = await fetchTimelineCards(doctorId);
      // fetchTimelineCards already returns normalized [{id, label, color}]
      return Array.isArray(list) ? list : [];
    },
    enabled: Boolean(doctorId),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (day-long cache)
    gcTime: 24 * 60 * 60 * 1000,    // 24 hours persistent retention
    meta: { persist: true },
    ...options,
  });
}

/**
 * Fetch timeline templates list for a doctor with 24-hour persistent caching.
 * Templates are doctor-scoped configuration, rarely modified during consultations.
 *
 * @param {number|string} doctorId - The doctor's unique identifier
 * @param {object} options - Additional TanStack Query options
 */
export function useTimelineTemplates(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.observations.templates(doctorId),
    queryFn: async () => {
      const list = await fetchTimelineTemplates(doctorId);
      // fetchTimelineTemplates already returns normalized [{id, name, steps_count}]
      return Array.isArray(list) ? list : [];
    },
    enabled: Boolean(doctorId),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (day-long cache)
    gcTime: 24 * 60 * 60 * 1000,    // 24 hours persistent retention
    meta: { persist: true },
    ...options,
  });
}

/**
 * Fetch a single timeline template detail (with steps) for a doctor.
 * Cached for 24 hours since template structure rarely changes.
 *
 * @param {number|string} doctorId - The doctor's unique identifier
 * @param {number|string} templateId - The template's unique identifier
 * @param {object} options - Additional TanStack Query options
 */
export function useTimelineTemplateDetail(doctorId, templateId, options = {}) {
  return useQuery({
    queryKey: queryKeys.observations.template(doctorId, templateId),
    queryFn: async () => {
      const detail = await fetchTimelineTemplate(doctorId, templateId);
      return detail ?? null;
    },
    enabled: Boolean(doctorId) && Boolean(templateId),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (day-long cache)
    gcTime: 24 * 60 * 60 * 1000,    // 24 hours persistent retention
    meta: { persist: true },
    ...options,
  });
}

/**
 * Imperative fetcher for a single timeline template detail.
 * Uses the queryClient cache when available, otherwise fetches directly.
 *
 * @param {number|string} doctorId - The doctor's unique identifier
 * @param {number|string} templateId - The template's unique identifier
 * @param {import('@tanstack/react-query').QueryClient} [queryClient] - Optional queryClient for cache integration
 */
export async function fetchTimelineTemplateQuery(doctorId, templateId, queryClient = null) {
  if (!doctorId || !templateId) return null;

  const queryFn = async () => {
    const detail = await fetchTimelineTemplate(doctorId, templateId);
    return detail ?? null;
  };

  if (queryClient) {
    return queryClient.fetchQuery({
      queryKey: queryKeys.observations.template(doctorId, templateId),
      queryFn,
      staleTime: 24 * 60 * 60 * 1000,
    });
  }

  return queryFn();
}

/**
 * Direct fetcher for doctor examinations / observations catalog
 */
export async function fetchDoctorExaminations(doctorId) {
  const response = await getDoctorExaminationsApi();
  const data = response?.data;
  const list = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data)
      ? data
      : [];

  return list.map((x, idx) => ({
    id: x.id ?? idx,
    item: x.item ?? String(x.item ?? ""),
    categorie: x.categorie ?? x.category ?? "Autres",
  }));
}

/**
 * Fetch doctor examinations / observations items with 24-hour persistent caching.
 * Doctor examinations are static and rarely modified during consultations.
 * Caching for 24h ensures fast, persistent lookups and eliminates redundant network calls.
 */
export function useDoctorExaminations(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.observations.examinations(doctorId || 'current'),
    queryFn: async () => {
      return fetchDoctorExaminations(doctorId);
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (day-long cache)
    gcTime: 24 * 60 * 60 * 1000,    // 24 hours persistent cache retention
    meta: {
      persist: true,
    },
    ...options,
  });
}

/**
 * Direct fetcher for doctor services
 */
export async function fetchDoctorServicesQuery(doctorId) {
  if (!doctorId) return [];
  const response = await fetchDoctorServices(doctorId);
  const list = Array.isArray(response)
    ? response
    : (response?.data?.data ?? response?.data ?? []);
  return list;
}

/**
 * Fetch doctor services with 24-hour (day-long) persistent caching.
 * Doctor services are rarely modified during the day, so keeping them cached
 * and persisted eliminates redundant network requests across observation sessions.
 */
export function useDoctorServices(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.observations.services(doctorId || 'current'),
    queryFn: async () => {
      return fetchDoctorServicesQuery(doctorId);
    },
    enabled: Boolean(doctorId),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (day-long)
    gcTime: 24 * 60 * 60 * 1000,    // 24 hours persistent cache retention
    meta: {
      persist: true,
    },
    ...options,
  });
}

// ============================================================================
// Mutation Hooks (with Optimistic Updates & Targeted Invalidation)
// ============================================================================

/**
 * Optimistic mutation for adding a new medical observation
 */
export function useAddObservation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => addObservationApi(variables),
    onMutate: async (newObservationPayload) => {
      const patientId = newObservationPayload?.patient_id;
      if (!patientId) return {};

      const queryKey = queryKeys.observations.byPatient(patientId);

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous state
      const previousObservations = queryClient.getQueryData(queryKey) || [];

      // Create optimistic entry
      const optimisticObservation = {
        id: `temp-${Date.now()}`,
        observation: newObservationPayload.observation || newObservationPayload.notes,
        created_at: new Date().toISOString(),
        is_pending: true,
        ...newObservationPayload,
      };

      // Optimistically update
      queryClient.setQueryData(queryKey, (old = []) => {
        return Array.isArray(old) ? [optimisticObservation, ...old] : [optimisticObservation];
      });

      return { previousObservations, queryKey, patientId };
    },
    onError: (err, newObservationPayload, context) => {
      // Rollback on error
      if (context?.previousObservations && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousObservations);
      }
      options.onError?.(err, newObservationPayload, context);
    },
    onSettled: (data, error, variables, context) => {
      // Invalidate specifically for this patient
      if (context?.patientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.observations.byPatient(context.patientId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.observations.timeline(context.patientId),
        });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Optimistic mutation for creating timeline observation
 */
export function useCreateTimelineObservation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, data }) => createTimelineObservation(patientId, data),
    onMutate: async ({ patientId, data }) => {
      const queryKey = queryKeys.observations.timeline(patientId);
      await queryClient.cancelQueries({ queryKey });

      const previousTimeline = queryClient.getQueryData(queryKey) || [];
      const optimisticItem = {
        id: `temp-${Date.now()}`,
        ...data,
        is_pending: true,
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData(queryKey, (old = []) => {
        return Array.isArray(old) ? [optimisticItem, ...old] : [optimisticItem];
      });

      return { previousTimeline, queryKey, patientId };
    },
    onError: (err, variables, context) => {
      if (context?.previousTimeline && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousTimeline);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.patientId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.observations.timeline(context.patientId) });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Optimistic mutation for updating timeline observation
 */
export function useUpdateTimelineObservation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, observationId, data }) =>
      updateTimelineObservation(patientId, observationId, data),
    onMutate: async ({ patientId, observationId, data }) => {
      const queryKey = queryKeys.observations.timeline(patientId);
      await queryClient.cancelQueries({ queryKey });

      const previousTimeline = queryClient.getQueryData(queryKey) || [];

      queryClient.setQueryData(queryKey, (old = []) => {
        if (!Array.isArray(old)) return old;
        return old.map((item) => (item.id === observationId ? { ...item, ...data } : item));
      });

      return { previousTimeline, queryKey, patientId };
    },
    onError: (err, variables, context) => {
      if (context?.previousTimeline && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousTimeline);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.patientId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.observations.timeline(context.patientId) });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Optimistic mutation for deleting timeline observation
 */
export function useDeleteTimelineObservation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ patientId, observationId }) =>
      deleteTimelineObservation(patientId, observationId),
    onMutate: async ({ patientId, observationId }) => {
      const queryKey = queryKeys.observations.timeline(patientId);
      await queryClient.cancelQueries({ queryKey });

      const previousTimeline = queryClient.getQueryData(queryKey) || [];

      queryClient.setQueryData(queryKey, (old = []) => {
        if (!Array.isArray(old)) return old;
        return old.filter((item) => item.id !== observationId);
      });

      return { previousTimeline, queryKey, patientId };
    },
    onError: (err, variables, context) => {
      if (context?.previousTimeline && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousTimeline);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.patientId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.observations.timeline(context.patientId) });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}
